"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, RotateCcw } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";

// Gallery item types with aspect ratio and positioning
interface GalleryItem {
  src: string;
  type: "image" | "video";
  alt?: string;
  // Aspect ratio options
  aspect: "portrait" | "landscape" | "square" | "tall";
  // Object position for cropping control (default: center)
  position?: "top" | "center" | "bottom";
}

// Base64 blur placeholder for faster loading
const BLUR_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAGCAYAAAD68A/GAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAIklEQVR4nGNgGPGAmZGR4T8DA8N/BgYGBmZGRob/yGwGXAAAVFwD/x5QLREAAAAASUVORK5CYII=";

// All gallery content - with customized aspect ratios and positioning
// position: "top" for portraits with faces, "tall" for videos needing full height
const GALLERY_ITEMS_RAW: GalleryItem[] = [
  // New brand showcase images & videos
  { src: "/gallery/gallery-1.jpg", type: "image", alt: "Gallery image 1", aspect: "portrait", position: "center" },
  { src: "/gallery/gallery-2.jpg", type: "image", alt: "Gallery image 2", aspect: "portrait", position: "top" },
  { src: "/gallery/gallery-3.jpg", type: "image", alt: "Gallery image 3", aspect: "square", position: "top" },
  { src: "/gallery/gallery-4.jpg", type: "image", alt: "Gallery image 4", aspect: "square", position: "top" },
  { src: "/gallery/gallery-5.jpg", type: "image", alt: "Gallery image 5", aspect: "landscape", position: "center" },
  { src: "/gallery/gallery-6.jpg", type: "image", alt: "Gallery image 6", aspect: "square", position: "center" },
  // gallery-7 removed (duplicate)
  { src: "/gallery/gallery-8.jpg", type: "image", alt: "Gallery image 8", aspect: "portrait", position: "center" },
  { src: "/gallery/gallery-9.jpg", type: "image", alt: "Gallery image 9", aspect: "portrait", position: "top" },
  { src: "/gallery/gallery-10.jpg", type: "image", alt: "Gallery image 10", aspect: "landscape", position: "center" },
  { src: "/gallery/gallery-11.jpg", type: "image", alt: "Gallery image 11", aspect: "landscape", position: "top" },
  { src: "/gallery/gallery-12.mp4", type: "video", alt: "Gallery video 12", aspect: "portrait", position: "center" },
  { src: "/gallery/gallery-13.png", type: "image", alt: "Gallery image 13", aspect: "landscape", position: "center" },
  { src: "/gallery/gallery-14.png", type: "image", alt: "Gallery image 14", aspect: "landscape", position: "center" },
  { src: "/gallery/gallery-15.mp4", type: "video", alt: "Gallery video 15", aspect: "tall", position: "center" },
  { src: "/gallery/gallery-16.mp4", type: "video", alt: "Gallery video 16", aspect: "portrait", position: "center" },
  { src: "/gallery/gallery-17.jpg", type: "image", alt: "Gallery image 17", aspect: "square", position: "center" },
  { src: "/gallery/gallery-18.jpg", type: "image", alt: "Gallery image 18", aspect: "square", position: "center" },
  { src: "/gallery/gallery-19.jpg", type: "image", alt: "Gallery image 19", aspect: "square", position: "center" },
  { src: "/gallery/gallery-20.jpg", type: "image", alt: "Gallery image 20", aspect: "landscape", position: "center" },
];

// Seeded shuffle to ensure consistent order across renders
function seededShuffle<T>(array: T[], seed: number): T[] {
  const result = [...array];
  let currentSeed = seed;

  const random = () => {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    return currentSeed / 233280;
  };

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
}

// Shuffled gallery items (consistent order)
const GALLERY_ITEMS = seededShuffle(GALLERY_ITEMS_RAW, 42);

// Aspect ratio CSS classes
const ASPECT_CLASSES: Record<GalleryItem["aspect"], string> = {
  portrait: "aspect-[2/3]", // Standard portrait (shows faces well)
  landscape: "aspect-[3/2]", // Standard landscape
  square: "aspect-square",
  tall: "aspect-[9/16]", // Very tall - for videos/images that need full height
};

// Object position classes
const POSITION_CLASSES: Record<string, string> = {
  top: "object-top",
  center: "object-center",
  bottom: "object-bottom",
};

// Format time for video player
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// Custom Video Player Component
function VideoPlayer({ src, aspect, position = "center" }: { src: string; aspect: GalleryItem["aspect"]; position?: GalleryItem["position"] }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isEnded, setIsEnded] = useState(false);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const progress = (video.currentTime / video.duration) * 100;
      setProgress(progress);
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setIsEnded(true);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
        setIsEnded(false);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const replay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
      setIsEnded(false);
    }
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * videoRef.current.duration;
    }
  };

  return (
    <div
      className={cn("relative w-full group", ASPECT_CLASSES[aspect])}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(isPlaying ? false : true)}
    >
      <video
        ref={videoRef}
        src={src}
        muted={isMuted}
        playsInline
        preload="metadata"
        className={cn("absolute inset-0 w-full h-full object-cover", POSITION_CLASSES[position || "center"])}
      />

      {/* Video Badge */}
      <div className="absolute top-2 left-2 sm:top-3 sm:left-3 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded bg-black/60 backdrop-blur-sm text-[9px] sm:text-[10px] font-medium z-10" style={{ color: "rgba(255,255,255,0.9)" }}>
        VIDEO
      </div>

      {/* Controls Overlay */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 p-2 sm:p-3 pb-3 sm:pb-4",
          "bg-gradient-to-t from-black via-black/70 via-40% to-transparent",
          "transition-opacity duration-300 z-10",
          showControls || !isPlaying || isEnded ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        )}
      >
        {/* Progress Bar */}
        <div
          className="w-full h-1 bg-white/20 rounded-full cursor-pointer mb-2"
          onClick={seek}
        >
          <motion.div
            className="h-full bg-gradient-to-r from-accent to-lime-400 rounded-full relative"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-white shadow-md" />
          </motion.div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={togglePlay}
            className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            {isPlaying ? (
              <Pause className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: "white" }} />
            ) : (
              <Play className="w-3 h-3 sm:w-4 sm:h-4 ml-0.5" style={{ color: "white" }} />
            )}
          </button>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={toggleMute}
              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              {isMuted ? (
                <VolumeX className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: "white" }} />
              ) : (
                <Volume2 className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: "white" }} />
              )}
            </button>
            <span className="text-[9px] sm:text-xs font-mono" style={{ color: "rgba(255,255,255,0.8)" }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>

      {/* Center Play/Replay Button */}
      {(!isPlaying || isEnded) && (
        <button
          onClick={isEnded ? replay : togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors hover:bg-black/20 z-10"
        >
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-foreground/20">
            {isEnded ? (
              <RotateCcw className="w-4 h-4 sm:w-6 sm:h-6" style={{ color: "white" }} />
            ) : (
              <Play className="w-4 h-4 sm:w-6 sm:h-6 ml-0.5" style={{ color: "white" }} />
            )}
          </div>
        </button>
      )}
    </div>
  );
}

// Gallery Card Component - preserves aspect ratio with custom positioning
function GalleryCard({
  item,
  index,
}: {
  item: GalleryItem;
  index: number;
}) {
  const positionClass = POSITION_CLASSES[item.position || "center"];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.2) }}
      className={cn(
        "relative rounded-lg sm:rounded-xl overflow-hidden",
        "border border-foreground/10 hover:border-foreground/20",
        "shadow-lg shadow-black/30",
        "bg-neutral-900",
        "transition-all duration-300",
        "hover:shadow-xl hover:shadow-accent/10"
      )}
    >
      {item.type === "video" ? (
        <VideoPlayer src={item.src} aspect={item.aspect} position={item.position} />
      ) : (
        <div className={cn("relative w-full", ASPECT_CLASSES[item.aspect])}>
          <Image
            src={item.src}
            alt={item.alt || "Gallery image"}
            fill
            loading="lazy"
            placeholder="blur"
            blurDataURL={BLUR_DATA_URL}
            className={cn("object-cover transition-transform duration-500 hover:scale-105", positionClass)}
            sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 22vw"
          />
          {/* Image Badge */}
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded bg-black/60 backdrop-blur-sm text-[9px] sm:text-[10px] font-medium z-10" style={{ color: "rgba(255,255,255,0.9)" }}>
            IMAGE
          </div>
        </div>
      )}
    </motion.div>
  );
}

// Hook to get responsive items per page
function useItemsPerPage() {
  const [itemsPerPage, setItemsPerPage] = useState(10); // Start with balanced desktop default

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setItemsPerPage(10); // lg/xl/2xl: 10 items (2 pages of 10/9) - Balanced
      } else if (width >= 768) {
        setItemsPerPage(6); // md (tablet): 6 items (4 pages of 6/6/6/1) - better than 8
      } else {
        setItemsPerPage(5); // mobile: 5 items (4 pages of 5/5/5/4) - Balanced
      }
    };

    updateItemsPerPage();
    window.addEventListener("resize", updateItemsPerPage);
    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, []);

  return itemsPerPage;
}

// Height weights for balancing columns (approximate height-to-width ratio)
const ASPECT_WEIGHTS: Record<GalleryItem["aspect"], number> = {
  tall: 1.75,     // ~16:9 vertical
  portrait: 1.5,  // 3:2 vertical
  square: 1,      // 1:1
  landscape: 0.66 // 2:3 vertical
};

// Function to distribute items into columns using greedy height balancing
// This ensures the bottom of the grid is as even ("rectangular") as possible
function distributeItems(items: GalleryItem[], columnCount: number) {
  const columns: GalleryItem[][] = Array.from({ length: columnCount }, () => []);
  const columnHeights = Array(columnCount).fill(0);

  items.forEach((item) => {
    // Find the column with the minimum current height
    const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
    
    // Add item to shortest column
    columns[shortestColumnIndex].push(item);
    
    // Update column height with the item's aspect ratio weight
    columnHeights[shortestColumnIndex] += ASPECT_WEIGHTS[item.aspect];
  });
  
  return columns;
}

// Main Gallery Component - Flex Masonry layout
export function ContentGallery() {
  const [activeSet, setActiveSet] = useState(0);
  const itemsPerPage = useItemsPerPage();
  const totalSets = Math.ceil(GALLERY_ITEMS.length / itemsPerPage);
  
  // Determine column count based on screen width (SSR safe)
  const [columnCount, setColumnCount] = useState(2); // Default mobile
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width >= 1280) setColumnCount(4); // xl+
      else if (width >= 768) setColumnCount(3); // md/lg
      else setColumnCount(2); // mobile
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  const currentItems = useMemo(() => {
    return GALLERY_ITEMS.slice(
      activeSet * itemsPerPage,
      (activeSet + 1) * itemsPerPage
    );
  }, [activeSet, itemsPerPage]);

  // Distribute items into columns for masonry layout
  const columns = useMemo(() => distributeItems(currentItems, columnCount), [currentItems, columnCount]);

  const goNext = useCallback(() => setActiveSet((prev) => (prev + 1) % totalSets), [totalSets]);
  const goPrev = useCallback(() => setActiveSet((prev) => (prev === 0 ? totalSets - 1 : prev - 1)), [totalSets]);

  // Reset to page 0 if current page exceeds total after resize
  useEffect(() => {
    if (activeSet >= totalSets) {
      setActiveSet(0);
    }
  }, [activeSet, totalSets]);

  return (
    <section id="gallery" className="relative overflow-hidden" style={{ paddingTop: 'clamp(4rem, 10vh, 8rem)', paddingBottom: 'clamp(4rem, 10vh, 8rem)' }}>
      {/* Ambient Glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[800px] sm:w-[1000px] md:w-[1200px] h-[500px] sm:h-[600px]"
          style={{
            background: `
              radial-gradient(ellipse 60% 50% at 30% 50%, rgba(16, 185, 129, 0.06) 0%, transparent 60%),
              radial-gradient(ellipse 40% 40% at 70% 60%, rgba(163, 230, 53, 0.03) 0%, transparent 50%)
            `,
            filter: "blur(80px)",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left mb-6 sm:mb-8"
        >
          <span className="text-accent text-sm font-medium uppercase tracking-wider mb-4 block">
            Gallery
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-4">
            <span className="text-gradient">Made with</span> Lateral
          </h2>
          <p className="text-foreground-secondary text-sm md:text-base leading-relaxed max-w-md">
            Real content created by our users.
          </p>
        </motion.div>

        {/* Mobile/Tablet: Navigation above gallery */}
        <div className="lg:hidden mb-6 sm:mb-8">
          <div className="flex items-center gap-0">
            <button
              onClick={goPrev}
              className={cn(
                "flex items-center justify-center mr-3 sm:mr-4",
                "w-8 h-8 sm:w-9 sm:h-9",
                "rounded-full border border-foreground/30 toolkit-arrow",
                "bg-transparent hover:bg-foreground/5 hover:border-foreground/40",
                "text-foreground/50 hover:text-foreground/80",
                "transition-all duration-200"
              )}
              aria-label="Previous set"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <div className="relative flex-1 h-[3px] bg-foreground/15 rounded-full overflow-hidden toolkit-progress">
              <motion.div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent to-lime-400 rounded-full"
                initial={false}
                animate={{ width: `${((activeSet + 1) / totalSets) * 100}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>

            <button
              onClick={goNext}
              className={cn(
                "flex items-center justify-center ml-3 sm:ml-4",
                "w-8 h-8 sm:w-9 sm:h-9",
                "rounded-full border border-foreground/30 toolkit-arrow",
                "bg-transparent hover:bg-foreground/5 hover:border-foreground/40",
                "text-foreground/50 hover:text-foreground/80",
                "transition-all duration-200"
              )}
              aria-label="Next set"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <div className="relative ml-4 sm:ml-6">
              <AnimatePresence mode="wait">
                <motion.span
                  key={activeSet}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="block text-5xl sm:text-6xl font-bold leading-none tabular-nums select-none"
                  style={{ color: "var(--section-index-color)" }}
                >
                  {String(activeSet + 1).padStart(2, "0")}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Desktop: Split Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Side: Watermark + Navigation (Desktop only) */}
          <div className="hidden lg:flex lg:flex-col lg:w-[200px] xl:w-[220px] flex-shrink-0 relative">
            {/* Navigation - At the top */}
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-4">
                <button
                  onClick={goPrev}
                  className={cn(
                    "w-10 h-10 rounded-full border border-foreground/30 toolkit-arrow",
                    "bg-transparent hover:bg-foreground/5 hover:border-foreground/40",
                    "text-foreground/50 hover:text-foreground/80",
                    "flex items-center justify-center transition-all duration-200"
                  )}
                  aria-label="Previous set"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goNext}
                  className={cn(
                    "w-10 h-10 rounded-full border border-foreground/30 toolkit-arrow",
                    "bg-transparent hover:bg-foreground/5 hover:border-foreground/40",
                    "text-foreground/50 hover:text-foreground/80",
                    "flex items-center justify-center transition-all duration-200"
                  )}
                  aria-label="Next set"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={activeSet}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="text-5xl xl:text-6xl font-bold leading-none tabular-nums select-none ml-auto"
                    style={{ color: "var(--section-index-color)" }}
                  >
                    {String(activeSet + 1).padStart(2, "0")}
                  </motion.span>
                </AnimatePresence>
              </div>

              <div className="relative w-full h-[3px] bg-foreground/15 rounded-full overflow-hidden toolkit-progress">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent to-lime-400 rounded-full"
                  initial={false}
                  animate={{ width: `${((activeSet + 1) / totalSets) * 100}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </div>
            </div>

            {/* Large Watermark - Below navigation, full height */}
            <div className="relative mt-6 flex-1 min-h-[500px] pointer-events-none select-none">
              <span
                className="absolute left-1/2 -translate-x-1/2 top-0 text-[120px] xl:text-[140px] font-bold leading-none tracking-tight whitespace-nowrap"
                style={{ color: "var(--section-index-color)", writingMode: "vertical-rl", textOrientation: "mixed", transform: "rotate(180deg)" }}
              >
                gallery
              </span>
            </div>
          </div>

          {/* Right Side: Gallery Grid - Flex Masonry */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSet}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex flex-row gap-3 sm:gap-4"
              >
                {columns.map((columnItems, columnIndex) => (
                  <div key={columnIndex} className="flex flex-col gap-3 sm:gap-4 flex-1">
                    {columnItems.map((item, itemIndex) => (
                      <GalleryCard 
                        key={`${item.src}-${columnIndex}-${itemIndex}`} 
                        item={item} 
                        index={itemIndex} 
                      />
                    ))}
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
